import { BrightLocalPanel, type BrightLocalClient } from "../components/BrightLocalSetup";
import { GoogleBusinessStatus } from "../components/GoogleBusinessConnect";
import Dialog from "../components/Dialog";
import FilterPill from "../components/FilterPill";
import { PROJECT_PRIORITIES, PROJECT_STATUSES, PROJECT_TYPES } from "../components/NewProjectDialog";
import { ExternalLink } from "lucide-react";
import { useClientSelection } from '../client-context';
import { AssetUpload } from '../components/AssetUpload';
import Markdown from "../components/Markdown";
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  Target,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate } from "../api";
import { sentence } from "../components/Workflow";
import {
  ErrorState,
  EmptyState,
  MetricCard,
  PageHeader,
  PageSkeleton,
  StatusBadge,
} from "../components/Ui";
import { useApi } from "../hooks";
import RecordForm, { type Field } from "../components/RecordForm";
import { useAuth } from "../auth";
import type { Client } from "../types";

type Row = Record<string, unknown>;
type Column = {
  key: string;
  label: string;
  render?: (value: unknown, row: Row) => ReactNode;
};
type DetailEndpoint = (row: Row) => string;
type DetailListKey = "messages" | "steps";

const extraDetailFields: Record<string, string> = {
  description: "Description",
  notes: "Notes",
  prompt_body: "Prompt",
  full_text: "Full text",
  exec_note: "Team note",
  performance_context: "Performance context",
  owner_name: "Owner",
  submitted_by_name: "Submitted by",
  client_summary: "Client summary",
  narrative_status: "Narrative status",
  answered_calls: "Answered calls",
  completed_jobs: "Completed jobs",
  content: "Lesson",
  practice_prompt: "Practice",
};

function safeLink(value: unknown) {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const url = new URL(value, window.location.origin);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function RecordDetails({
  row,
  columns,
  detailEndpoint,
  detailListKey = "messages",
  onClose,
  onEdit,
}: {
  row: Row;
  columns: Column[];
  detailEndpoint?: DetailEndpoint;
  detailListKey?: DetailListKey;
  onClose: () => void;
  onEdit?: (row: Row) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [detail, setDetail] = useState<Row>(row);
  const [loading, setLoading] = useState(Boolean(detailEndpoint));
  const [error, setError] = useState<string | null>(null);
  const endpoint = detailEndpoint?.(row);
  const title = String(row[columns[0].key] || "Record details");

  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => {
      if (element?.open) element.close();
    };
  }, []);
  useEffect(() => {
    if (!endpoint) return;
    let active = true;
    api<Row | Row[]>(endpoint)
      .then((result) => {
        if (!active) return;
        if (Array.isArray(result))
          setDetail({ ...row, [detailListKey]: result });
        else
          setDetail({
            ...row,
            ...result,
            ...(result.key_metrics && typeof result.key_metrics === "object"
              ? (result.key_metrics as Row)
              : {}),
          });
      })
      .catch((reason) => {
        if (active)
          setError(
            reason instanceof Error
              ? reason.message
              : "Details could not be loaded.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [endpoint, detailListKey]);

  const fields = [
    ...columns.map((column) => ({
      key: column.key,
      label: column.label,
      render: column.render,
    })),
    ...Object.entries(extraDetailFields)
      .filter(
        ([key]) =>
          !columns.some((column) => column.key === key) &&
          detail[key] !== undefined,
      )
      .map(([key, label]) => ({ key, label, render: undefined })),
  ];
  const phases = Array.isArray(detail.phases) ? (detail.phases as Row[]) : [];
  const messages = Array.isArray(detail.messages)
    ? (detail.messages as Row[])
    : [];
  const steps = Array.isArray(detail.steps) ? (detail.steps as Row[]) : [];
  const fileUrl = safeLink(detail.file_url);
  const versions = useApi<Row[]>(
    detail.prompt_body !== undefined
      ? `/api/prompts/${detail.id}/versions`
      : null,
  );

  return (
    <dialog
      ref={dialog}
      className="record-dialog"
      aria-label={title}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target !== dialog.current) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
      }}
    >
      <div className="record-dialog-head">
        <div>
          <p className="eyebrow">Record details</p>
          <h2>{title}</h2>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Close details"
          onClick={onClose}
        >
          <X aria-hidden="true" />
        </button>
      </div>
      <div className="record-dialog-content">
        {onEdit && (
          <button className="button secondary" onClick={() => onEdit(detail)}>
            Edit record
          </button>
        )}
        {detail.prompt_body !== undefined && (
          <Link
            className="button secondary"
            to={`/analyst?prompt_id=${detail.id}`}
            onClick={onClose}
          >
            Use in Analyst
          </Link>
        )}
        {loading && (
          <p className="muted" role="status">
            Loading full details…
          </p>
        )}
        {error && (
          <p className="inline-notice danger" role="alert">
            {error} Showing available summary.
          </p>
        )}
        <dl className="record-fields">
          {fields
            .filter(
              (field) =>
                detail[field.key] !== undefined &&
                detail[field.key] !== null &&
                detail[field.key] !== "",
            )
            .map((field) => (
              <div key={field.key}>
                <dt>{field.label}</dt>
                <dd>
                  {field.render
                    ? field.render(detail[field.key], detail)
                    : displayValue(field.key, detail[field.key])}
                </dd>
              </div>
            ))}
        </dl>
        {versions.data && versions.data.length > 0 && (
          <details className="record-subsection">
            <summary>Version history ({versions.data.length})</summary>
            {versions.data.map((version) => (
              <article key={String(version.id)}>
                <h3>Version {String(version.version_number || version.id)}</h3>
                <Markdown>{String(version.prompt_body || "")}</Markdown>
              </article>
            ))}
          </details>
        )}
        {fileUrl && (
          <a
            className="button secondary record-file-link"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open asset <ArrowUpRight aria-hidden="true" />
          </a>
        )}
        {phases.length > 0 && (
          <section className="record-subsection">
            <h3>Phases</h3>
            <ol>
              {phases.map((phase, index) => (
                <li key={String(phase.id ?? index)}>
                  <strong>
                    {String(phase.phase_name || `Phase ${index + 1}`)}
                  </strong>
                  <span>
                    {valueLabel(phase.status || "pending")} ·{" "}
                    {Number(phase.tasks_done || 0)} of{" "}
                    {Number(phase.task_count || 0)} tasks done
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}
        {messages.length > 0 && (
          <section className="record-subsection">
            <h3>Messages</h3>
            <ol>
              {messages.map((message, index) => (
                <li key={String(message.id ?? index)}>
                  <strong>{String(message.role || "Message")}</strong>
                  <span>
                    {String(
                      message.content_text ||
                        message.content ||
                        message.message ||
                        "No message content recorded.",
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}
        {steps.length > 0 && (
          <section className="record-subsection">
            <h3>Checklist steps</h3>
            <ol>
              {steps.map((step, index) => (
                <li key={String(step.id ?? index)}>
                  <strong>
                    {Number(step.step_number || index + 1)}.{" "}
                    {String(step.title || "Step")}
                  </strong>
                  {Boolean(step.description) && (
                    <span>{String(step.description)}</span>
                  )}
                  {Boolean(step.kb_reference) && (
                    <span>Reference: {String(step.kb_reference)}</span>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </dialog>
  );
}

function rowsFrom(data: unknown, rowKey?: string): Row[] {
  if (Array.isArray(data)) return data as Row[];
  if (rowKey && data && typeof data === "object") {
    const rows = (data as Row)[rowKey];
    return Array.isArray(rows) ? (rows as Row[]) : [];
  }
  return [];
}

function toneFor(
  value: unknown,
): "neutral" | "success" | "warning" | "danger" | "info" {
  const normalized = String(value || "").toLowerCase();
  if (
    /^(completed?|active|approved|healthy|ready|on.track|resolved|verified|posted)$/.test(
      normalized,
    )
  )
    return "success";
  if (/pending|open|watch|progress|paused|draft|medium|due/.test(normalized))
    return "warning";
  if (
    /critical|high|risk|overdue|blocked|rejected|failed|inactive/.test(
      normalized,
    )
  )
    return "danger";
  if (/new|review|info|queued/.test(normalized)) return "info";
  return "neutral";
}

/** A stored code as words: "gbp_post" → "GBP post", "in_progress" → "In progress". */
const VALUE_WORDS: Record<string, string> = { broll: "B-roll" };
function valueLabel(value: unknown) {
  const raw = String(value);
  return VALUE_WORDS[raw.toLowerCase()] || sentence(raw);
}

function displayValue(key: string, value: unknown) {
  if (value === null || value === undefined || value === "")
    return <span className="muted">—</span>;
  if (
    typeof value === "string" &&
    [
      "prompt_body",
      "full_text",
      "content",
      "description",
      "notes",
      "practice_prompt",
    ].includes(key)
  )
    return <Markdown>{value}</Markdown>;
  if (typeof value === "boolean")
    return (
      <StatusBadge tone={value ? "success" : "neutral"}>
        {value ? "Yes" : "No"}
      </StatusBadge>
    );
  // A badge only for a real state (status, risk). A type, priority or tier is a
  // plain word: three badges a row read as noise. sentence() keeps GBP, AI, SMS.
  if (/(^|_)(status|risk|risk_level)$/.test(key))
    return (
      <StatusBadge tone={toneFor(value)}>
        {valueLabel(value)}
      </StatusBadge>
    );
  if (/(^|_)(type|priority|tier)$/.test(key) && typeof value === "string")
    return valueLabel(value);
  if (
    /(_at|_date|^date$|^created$|^updated$|^due$)/.test(key) &&
    typeof value === "string"
  )
    return formatDate(value);
  if (typeof value === "number")
    return <span className="number-cell">{value.toLocaleString()}</span>;
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object")
    return (
      <span className="table-secondary">{JSON.stringify(value, null, 2)}</span>
    );
  return String(value);
}

function displayPercentage(value: unknown) {
  const numericValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numericValue)) return displayValue("rate", value);
  return (
    <span className="number-cell">
      {numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}%
    </span>
  );
}

function collectionForm(
  endpoint: string,
  row?: Row,
): { fields: Field[]; endpoint: string; method: string; title: string } | null {
  const field = (
    key: string,
    label: string,
    type: Field["type"] = "text",
    options?: string[],
    required = false,
  ): Field => ({ key, label, type, options, required });
  const common = [
    field("title", "Title", "text", undefined, true),
    field("description", "Description", "textarea"),
  ];
  // Every priority the API accepts (a Critical project opened on Medium and saved
  // as Medium); a project with none starts on Medium, as the API treats it.
  const priority: Field = { ...field("priority", "Priority", "select", PROJECT_PRIORITIES), default: "medium" };
  let fields: Field[] = [];
  // A client page lists "/api/projects?client_id=15": the form and its target
  // ("/api/projects/7") come from the path, never from the query.
  const base = endpoint.split("?")[0];
  let target = base;
  let title = "record";
  if (base === "/api/projects") {
    title = "project";
    fields = [
      field("name", "Project name", "text", undefined, true),
      field("project_type", "Project type", "select", PROJECT_TYPES),
      field("description", "Description", "textarea"),
      field("client_id", "Client (optional)", "number"),
      field("due_date", "Due date", "date"),
      priority,
    ];
    if (row) {
      fields = fields.filter(
        (field) => !["client_id", "project_type"].includes(field.key),
      );
      fields.push(field("status", "Status", "select", PROJECT_STATUSES));
    }
  } else if (base.startsWith("/api/assets/")) {
    if (row) return null;
    title = "asset link";
    fields = [
      field("file_name", "Asset name", "text", undefined, true),
      field("file_url", "File URL", "url", undefined, true),
      field("asset_type", "Type", "select", [
        "image",
        "logo",
        "video",
        "broll",
        "ad_creative",
        "copy_example",
        "messaging_template",
        "approved_script",
        "review_evidence",
        "citation_screenshot",
        "gbp_photo",
        "profile_asset",
      ]),
      field("platform", "Platform"),
      field("exec_note", "Team note", "textarea"),
      field("performance_context", "Performance context", "textarea"),
    ];
  } else return null;
  if (row && base !== "/api/kb" && !target.endsWith("/status"))
    target += `/${row.id}`;
  return {
    fields,
    endpoint: target,
    method: row ? "PUT" : "POST",
    // An asset link is added, not created: the file itself lives elsewhere.
    title: !row && base.startsWith("/api/assets/") ? "Add a link" : `${row ? "Edit" : "New"} ${title}`,
  };
}

function CollectionBody({
  endpoint,
  columns,
  emptyTitle,
  emptyDescription,
  emptyAction,
  canCreate = true,
  rowKey,
  detailEndpoint,
  detailListKey,
}: {
  endpoint: string;
  columns: Column[];
  emptyTitle: string;
  emptyDescription: string;
  /** The button an empty list offers, e.g. New project. */
  emptyAction?: ReactNode;
  /** False when the page has its own create button (the generic form is still used to edit). */
  canCreate?: boolean;
  rowKey?: string;
  detailEndpoint?: DetailEndpoint;
  detailListKey?: DetailListKey;
}) {
  const request = useApi<unknown>(endpoint);
  const { user } = useAuth();
  const canEditLibrary =
    !["/api/prompts", "/api/kb"].includes(endpoint) ||
    user?.role === "superadmin";
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const tableTop = useRef<HTMLDivElement>(null);
  const isLibrary = ["/api/prompts", "/api/kb"].includes(endpoint);
  // "No active projects" → "projects": the noun the empty state already uses names the search and the count.
  // "No rocks set" → "rocks", "No SOPs available" → "SOPs": the trailing state word is not part of the noun.
  const noun = String(emptyTitle || "records").replace(/^No\s+(active|matching|recorded)?\s*/i, "").replace(/\s+(set|yet|available|for this client)$/i, "").trim()
    .split(/\s+/).map((w) => (/[A-Z].*[A-Z]/.test(w) ? w : w.toLowerCase())).join(" ") || "records";
  const searchLabel = endpoint === "/api/prompts" ? "Search prompts" : endpoint === "/api/kb" ? "Search knowledge base" : `Search ${noun}`;
  const pageSize = 25;
  const [editing, setEditing] = useState<Row | "new" | null>(null);
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Row | null>(null);
  // A record opened from a link (?record=7) closes for good: left in the URL, it
  // reopened after every reload, including the one after saving an edit.
  const closeRecord = () => {
    setSelected(null);
    if (params.has("record")) setParams((current) => { const next = new URLSearchParams(current); next.delete("record"); return next; }, { replace: true });
  };
  useEffect(() => setSelected(null), [endpoint]);
  const rows = useMemo(
    () => rowsFrom(request.data, rowKey),
    [request.data, rowKey],
  );
  useEffect(() => {
    const id = params.get("record");
    if (id)
      setSelected(
        rows.find((row) => String(row.id ?? row.doc_code) === id) || null,
      );
  }, [params, rows]);
  const form = editing
    ? collectionForm(endpoint, editing === "new" ? undefined : editing)
    : null;
  const categories = [...new Set(rows.map(row => String(row.category_name || "")).filter(Boolean))].sort();
  const filtered = useMemo(() => {
    const categoryRows = category ? rows.filter(row => row.category_name === category) : rows;
    const needle = query.trim().toLowerCase();
    if (!needle) return categoryRows;
    return categoryRows.filter((row) =>
      columns.some((column) =>
        String(row[column.key] ?? "")
          .toLowerCase()
          .includes(needle),
      ),
    );
  }, [columns, query, rows, category]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const hasFilters = Boolean(query.trim() || category);
  const clearFilters = () => { setQuery(""); setCategory(""); setPage(1); };
  const changePage = (next: number) => {
    setPage(next);
    tableTop.current?.focus({ preventScroll: true });
    tableTop.current?.scrollIntoView({ block: "start" });
  };
  const attention = rows.filter((row) =>
    /pending|overdue|risk|critical|high|blocked|open/.test(
      String(row.status ?? row.priority ?? row.risk_level ?? "").toLowerCase(),
    ),
  ).length;

  if (request.loading) return <PageSkeleton cards={3} />;
  if (request.error)
    return <ErrorState message={request.error} onRetry={request.reload} />;

  return (
    <>
      {(isLibrary || attention > 0) && <p className="collection-summary"><strong>{rows.length}</strong> {isLibrary ? (endpoint === "/api/prompts" ? "prompts" : "documents") : "records"}{attention > 0 && <> · {attention} need attention</>}{isLibrary && <> · Open a record to read its full content.</>}</p>}
      {endpoint.startsWith('/api/assets/') && ['superadmin','exec'].includes(user?.role || '') && <AssetUpload key={endpoint} clientId={Number(endpoint.split('/').pop())} onDone={request.reload} />}
      <section className="panel module-panel">
        <div className="toolbar collection-toolbar" ref={tableTop} tabIndex={-1}>
          {canCreate && canEditLibrary && collectionForm(endpoint) && (
            <button
              className="button primary"
              onClick={() => setEditing("new")}
            >
              {collectionForm(endpoint)?.title}
            </button>
          )}
          <label className="search-control">
            <Search aria-hidden="true" />
            <span className="sr-only">{searchLabel}</span>
            <input
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(1); }}
              placeholder={searchLabel}
              type="search"
            />
          </label>
          {categories.length > 1 && <label className="select-control">
            <span className="sr-only">Filter by category</span>
            <select value={category} onChange={event => { setCategory(event.target.value); setPage(1); }}>
              <option value="">All categories</option>
              {categories.map(value => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>}
          {hasFilters && (
            <button className="text-button" onClick={clearFilters}>
              Clear filters
            </button>
          )}
          <span className="result-count" role="status" aria-live="polite">
            {hasFilters ? `${filtered.length} of ${rows.length}` : `${rows.length} ${noun}`}
          </span>
          <button
            className="icon-button"
            onClick={request.reload}
            aria-label="Refresh data"
          >
            <RefreshCw aria-hidden="true" />
          </button>
        </div>
        {filtered.length === 0 ? (
          <div className="collection-empty">
            <EmptyState title={hasFilters ? `No matching ${noun}` : emptyTitle} description={hasFilters ? `Try a different search or clear your filters to see all ${noun}.` : emptyDescription} action={hasFilters ? undefined : emptyAction} />
            {hasFilters && <button className="button secondary" onClick={clearFilters}>Show all {noun}</button>}
          </div>
        ) : (
          <div className="table-wrap">
            <table className={`module-table${isLibrary ? " library-table" : ""}`}>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  <th>
                    <span className="sr-only">Open details</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, index) => (
                  <tr
                    key={String(
                      row.id ?? row.doc_code ?? `${endpoint}-${index}`,
                    )}
                  >
                    {columns.map((column, columnIndex) => (
                      <td key={column.key} data-label={column.label}>
                        {columnIndex === 0 ? (
                          <button
                            className="record-open"
                            onClick={() => setSelected(row)}
                          >
                            {column.render
                              ? column.render(row[column.key], row)
                              : displayValue(column.key, row[column.key])}
                          </button>
                        ) : column.render ? (
                          column.render(row[column.key], row)
                        ) : (
                          displayValue(column.key, row[column.key])
                        )}
                      </td>
                    ))}
                    <td data-label="Details">
                      <button
                        className="row-action"
                        aria-label={`Open details for ${String(row[columns[0].key] || "record")}`}
                        onClick={() => setSelected(row)}
                      >
                        <ChevronRight aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > pageSize && <nav className="collection-pagination" aria-label="Record pages">
          <span role="status">Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}</span>
          <div className="pagination-actions">
            <button className="button secondary" disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)}><ChevronLeft aria-hidden="true" />Previous</button>
            <span>Page {currentPage} of {pageCount}</span>
            <button className="button secondary" disabled={currentPage === pageCount} onClick={() => changePage(currentPage + 1)}>Next<ChevronRight aria-hidden="true" /></button>
          </div>
        </nav>}
      </section>
      {form && (
        <RecordForm
          {...form}
          initial={editing === "new" ? {} : editing || {}}
          onClose={() => setEditing(null)}
          onSaved={request.reload}
        />
      )}
      {selected && (
        <RecordDetails
          row={selected}
          columns={columns}
          detailEndpoint={detailEndpoint}
          detailListKey={detailListKey}
          onClose={closeRecord}
          onEdit={
            canEditLibrary && collectionForm(endpoint, selected)
              ? (detail) => {
                  setEditing(detail);
                  closeRecord();
                }
              : undefined
          }
        />
      )}
    </>
  );
}

function CollectionPage({
  eyebrow,
  title,
  description,
  endpoint,
  columns,
  emptyTitle,
  emptyDescription,
  rowKey,
  detailEndpoint,
  detailListKey,
}: {
  eyebrow: string;
  title: string;
  description: string;
  endpoint: string;
  columns: Column[];
  emptyTitle: string;
  emptyDescription: string;
  rowKey?: string;
  detailEndpoint?: DetailEndpoint;
  detailListKey?: DetailListKey;
}) {
  return (
    <>
      <PageHeader eyebrow={eyebrow} title={title} description={description} />
      <CollectionBody
        key={endpoint}
        endpoint={endpoint}
        columns={columns}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        rowKey={rowKey}
        detailEndpoint={detailEndpoint}
        detailListKey={detailListKey}
      />
    </>
  );
}

function ClientPicker({
  clients,
  value,
  onChange,
}: {
  clients: Client[];
  value: number | null;
  onChange: (value: number) => void;
}) {
  return (
    <label className="select-control client-picker">
      <span>Client</span>
      <select
        value={value ?? ""}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {clients.map((client) => (
          <option key={client.id} value={client.id}>
            {client.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function ClientCollectionPage({
  eyebrow,
  title,
  description,
  endpoint,
  columns,
  emptyTitle,
  emptyDescription,
  detailEndpoint,
}: {
  eyebrow: string;
  title: string;
  description: string;
  endpoint: (clientId: number) => string;
  columns: Column[];
  emptyTitle: string;
  emptyDescription: string;
  detailEndpoint?: (clientId: number, row: Row) => string;
}) {
  const { clients, clientId, setClientId } = useClientSelection();
  return (
    <>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
      />
      {clients.loading ? (
        <PageSkeleton cards={3} />
      ) : clients.error ? (
        <ErrorState message={clients.error} onRetry={clients.reload} />
      ) : clientId === null ? (
        <section className="panel">
          <EmptyState
            title="No clients available"
            description="Client records will appear here once you have access to a client."
          />
        </section>
      ) : (
        <CollectionBody
          key={clientId}
          endpoint={endpoint(clientId)}
          columns={columns}
          emptyTitle={emptyTitle}
          emptyDescription={emptyDescription}
          detailEndpoint={
            detailEndpoint ? (row) => detailEndpoint(clientId, row) : undefined
          }
        />
      )}
    </>
  );
}

const statusColumn: Column = { key: "status", label: "Status" };

/**
 * One client's projects (the Projects page's List tab, under the client's name
 * in the sidebar). The page header holds New project, so the list's own create
 * button stays hidden; Edit record still opens from a project's details.
 */
export function ProjectsList({ clientId, clientName, newProject }: { clientId: number; clientName: string; newProject?: ReactNode }) {
  return (
    <CollectionBody
      endpoint={`/api/projects?client_id=${clientId}`}
      detailEndpoint={(row) => `/api/projects/${row.id}`}
      columns={[
        { key: "name", label: "Project" },
        { key: "project_type", label: "Type" },
        statusColumn,
        { key: "due_date", label: "Due" },
      ]}
      emptyTitle="No projects yet"
      emptyDescription={`Start the first project for ${clientName}.`}
      emptyAction={newProject}
      canCreate={false}
    />
  );
}

/**
 * Creative hub, Brand files tab: the client's asset links and private NOLA files.
 * The hub (pages/CreativeWorkspace.tsx) draws the one page header.
 */
export function CreativeHubPage({ clientId }: { clientId: number }) {
  return (
    <CollectionBody
      key={clientId}
      endpoint={`/api/assets/${clientId}`}
      columns={[
        { key: "file_name", label: "Asset" },
        { key: "asset_type", label: "Type" },
        { key: "platform", label: "Platform" },
        { key: "performance_context", label: "Context" },
        { key: "created_at", label: "Added" },
      ]}
      emptyTitle="No assets for this client"
      emptyDescription="Add a link to a brand file, or store a file privately in NOLA."
    />
  );
}

// ── Reviews & citations ──────────────────────────────────────────────
// One request set for the whole page (BrightLocal, stats, citations, the first
// page of reviews), so nothing arrives late and pushes the page down.

type ReviewSummary = { total: number; avg_rating: number | null; last_30_days: number; low_rated: number; reply_needed: number };
type ReviewStats = { summary?: ReviewSummary; by_location?: Array<{ location_id: number; market_name: string; total: number }> };

const REVIEW_PAGE = 20;
const REVIEW_PLATFORM: Record<string, string> = { google: "Google", yelp: "Yelp", facebook: "Facebook", bbb: "BBB", trustpilot: "Trustpilot", nextdoor: "Nextdoor" };
const reviewPlatform = (value: unknown) => REVIEW_PLATFORM[String(value || "").toLowerCase()] || "Other";
// Every reply status the database allows: a review already posted must not read as (or save back as) the first option.
const REPLY_STATUSES = ["pending", "drafted", "approved", "posted", "skipped", "not_needed"];
const REPLY_LABEL: Record<string, string> = { pending: "Reply needed", drafted: "Drafted", approved: "Approved", posted: "Posted", skipped: "Skipped", not_needed: "Not needed" };
const starsText = (rating: number) => `${rating} ${rating === 1 ? "star" : "stars"}`;

/** Which reviews the list shows: every one, 3 stars or less, or the ones waiting on a reply. */
type ReviewFilter = "" | "low" | "reply";

/** Reviews a page at a time, newest first. "Show more" adds the next page under the ones shown. */
function useReviewPages(clientId: number, filter: ReviewFilter, revision: number) {
  const path = `/api/reviews?client_id=${clientId}&limit=${REVIEW_PAGE}${filter === "low" ? "&rating_max=3" : filter === "reply" ? "&response_status=pending" : ""}`;
  const [rows, setRows] = useState<Row[]>([]);
  const [state, setState] = useState({ loading: true, loaded: false, more: false, error: "" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [retry, setRetry] = useState(0);
  const shownPath = useRef(path);
  useEffect(() => {
    let active = true;
    shownPath.current = path;
    setState((s) => ({ ...s, loading: true, error: "" }));
    api<Row[]>(`${path}&offset=0`)
      .then((page) => {
        if (!active) return;
        setRows(page);
        setState({ loading: false, loaded: true, more: page.length === REVIEW_PAGE, error: "" });
      })
      .catch((reason) => {
        if (active) setState({ loading: false, loaded: true, more: false, error: reason instanceof Error ? reason.message : "Reviews could not load." });
      });
    return () => { active = false; };
  }, [path, revision, retry]);
  async function loadMore() {
    setLoadingMore(true);
    try {
      const page = await api<Row[]>(`${path}&offset=${rows.length}`);
      if (shownPath.current !== path) return; // the filter changed meanwhile: these rows belong to the old list
      const seen = new Set(rows.map((row) => row.id));
      setRows([...rows, ...page.filter((row) => !seen.has(row.id))]);
      setState((s) => ({ ...s, more: page.length === REVIEW_PAGE, error: "" }));
    } catch (reason) {
      setState((s) => ({ ...s, error: reason instanceof Error ? reason.message : "More reviews could not load." }));
    } finally {
      setLoadingMore(false);
    }
  }
  return { rows, setRows, ...state, loadingMore, loadMore, retry: () => setRetry((n) => n + 1) };
}

/**
 * A review and its reply: the review itself, a reply box with "Draft with AI",
 * the reply status, team notes, and "Mark as posted" once the reply is live.
 */
function ReviewDialog({ review, onClose, onSaved }: { review: Row; onClose: () => void; onSaved: (patch: Row) => void }) {
  const saved = String(review.response_status || "not_needed");
  const [reply, setReply] = useState(String(review.response_text || ""));
  const [status, setStatus] = useState(saved);
  const [notes, setNotes] = useState(String(review.internal_notes || ""));
  const [busy, setBusy] = useState<"" | "draft" | "save" | "post" | "publish" | "verify">("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [attorneyApproved, setAttorneyApproved] = useState(false);
  const rating = Number(review.rating) || 0;
  const platform = reviewPlatform(review.platform);
  const text = String(review.review_text || "").trim();
  const id = String(review.id);
  const connection = useApi<{ ready: boolean; reason?: string; location_title?: string; requires_attorney_approval?: boolean }>(`/api/reviews/${id}/response/connection`);
  const fail = (reason: unknown) => setError(reason instanceof Error ? reason.message : "Could not save");

  async function draft() {
    if (reply.trim() && reply !== String(review.response_text || "") && !window.confirm("Replace your reply with an AI draft?")) return;
    setBusy("draft");
    setError("");
    try {
      const result = await api<{ draft: string }>(`/api/reviews/${id}/response/draft`, { method: "POST", body: "{}" });
      setReply(result.draft);
      setAttorneyApproved(false);
      setStatus("drafted");
      onSaved({ response_text: result.draft, response_status: "drafted" });
    } catch (reason) { fail(reason); } finally { setBusy(""); }
  }
  async function markPosted() {
    if (!window.confirm(`Confirm you already posted this reply on ${platform === "Other" ? "the review site" : platform}. This only records that action in NOLA.`)) return;
    setBusy("post");
    setError("");
    try {
      await api(`/api/reviews/${id}/response/post`, { method: "PUT", body: JSON.stringify({ response_text: reply }) });
      if (notes !== String(review.internal_notes || "")) await api(`/api/reviews/${id}`, { method: "PUT", body: JSON.stringify({ internal_notes: notes }) });
      onSaved({ response_text: reply, response_status: "posted", response_source: "manual", internal_notes: notes });
      onClose();
    } catch (reason) { fail(reason); } finally { setBusy(""); }
  }
  async function publish() {
    if (!window.confirm(`Publish this reply publicly on Google for ${connection.data?.location_title || "this location"}?${saved === "posted" ? " This replaces the existing Google reply." : ""}`)) return;
    setBusy("publish"); setError("");
    try {
      const result = await api<Row>(`/api/reviews/${id}/response/publish`, { method: "POST", body: JSON.stringify({ response_text: reply, confirm_publish: true, attorney_approved: attorneyApproved }) });
      onSaved(result); onClose();
    } catch (reason) { fail(reason); connection.reload(); } finally { setBusy(""); }
  }
  async function verify() {
    setBusy("verify"); setError(""); setNotice("");
    try {
      const result = await api<Row>(`/api/reviews/${id}/response/verify`, { method: "POST", body: "{}" });
      if (result.confirmed) { onSaved(result); setStatus("posted"); setReply(String(result.response_text)); }
      setNotice(String(result.message || "Google reply checked.")); connection.reload();
    } catch (reason) { fail(reason); } finally { setBusy(""); }
  }
  async function save() {
    // "Posted" also records when and by whom, so it goes the posting way.
    if (status === "posted" && saved !== "posted") return markPosted();
    setBusy("save");
    setError("");
    try {
      const nextStatus = status === "posted" && reply !== String(review.response_text || "") ? "drafted" : status;
      await api(`/api/reviews/${id}`, { method: "PUT", body: JSON.stringify({ response_text: reply, ...(nextStatus !== "posted" ? { response_status: nextStatus } : {}), internal_notes: notes }) });
      onSaved({ response_text: reply, response_status: nextStatus, internal_notes: notes });
      onClose();
    } catch (reason) { fail(reason); } finally { setBusy(""); }
  }

  return (
    <Dialog
      title={String(review.reviewer_name || "Anonymous reviewer")}
      description={`${starsText(rating)} on ${platform}, ${formatDate(String(review.review_date || ""))}`}
      onClose={() => { if (!busy) onClose(); }}
    >
      <blockquote className="rv-quote">{text || <span className="muted">Rating only. The reviewer wrote no text.</span>}</blockquote>
      <form className="operation-form record-form" onSubmit={(event) => { event.preventDefault(); void save(); }}>
        <label>
          <span className="field-label">Reply</span>
          <textarea rows={5} maxLength={4096} value={reply} onChange={(event) => { setReply(event.target.value); setAttorneyApproved(false); }} />
          <small className="field-hint">Review and edit the AI draft before publishing. Saving a draft keeps it in NOLA.</small>
        </label>
        <div className="rv-draft-row">
          <button type="button" className="button secondary" disabled={Boolean(busy)} onClick={() => void draft()}>
            {busy === "draft" ? "Drafting…" : "Draft with AI"}
          </button>
        </div>
        {connection.data?.ready ? <p className="muted">Ready to publish to {connection.data.location_title}. {saved === "posted" ? "Publishing replaces the current Google reply." : "Publishing sends this reply publicly to Google."}</p> : <p className="inline-notice">{connection.error || connection.data?.reason || "Checking Google connection…"} {platform === "Google" && <Link to={`/client-settings?client_id=${review.client_id}&section=google-business`}>Manage Google connection</Link>}</p>}
        {platform === "Google" && Boolean(review.google_review_resource) && <button type="button" className="button secondary" disabled={Boolean(busy)} onClick={() => void verify()}>{busy === "verify" ? "Checking…" : "Check reply on Google"}</button>}
        {notice && <p role="status">{notice}</p>}
        {connection.data?.requires_attorney_approval && <label><span><input type="checkbox" checked={attorneyApproved} onChange={event => setAttorneyApproved(event.target.checked)} /> An attorney approved this exact reply</span></label>}
        <label>
          <span className="field-label">Reply status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            {REPLY_STATUSES.map((value) => <option key={value} value={value}>{REPLY_LABEL[value]}</option>)}
          </select>
        </label>
        <label>
          <span className="field-label">Team notes</span>
          <textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" disabled={Boolean(busy)} onClick={onClose}>Cancel</button>
          {saved !== "posted" && (
            <button type="button" className="button secondary" disabled={Boolean(busy) || !reply.trim()} onClick={() => void markPosted()}>
              {busy === "post" ? "Saving…" : "Mark posted externally"}
            </button>
          )}
          <button className="button primary" disabled={Boolean(busy)}>{busy === "save" ? "Saving…" : "Save"}</button>
          {connection.data?.ready && <button type="button" className="button primary" disabled={Boolean(busy) || !reply.trim() || Boolean(connection.data.requires_attorney_approval && !attorneyApproved)} onClick={() => void publish()}>{busy === "publish" ? "Publishing…" : "Publish to Google"}</button>}
        </div>
      </form>
    </Dialog>
  );
}

function ReputationData({ clientId }: { clientId: number }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  // ?reply=needed (from a negative-review alert) or ?rating=low (from the tile).
  const filter: ReviewFilter = params.get("reply") === "needed" ? "reply" : params.get("rating") === "low" ? "low" : "";
  const lowOnly = filter === "low";
  const brightLocal = useApi<BrightLocalClient>(`/api/brightlocal/clients/${clientId}`);
  const stats = useApi<ReviewStats>(`/api/reviews/stats?client_id=${clientId}`);
  const citations = useApi<Row[]>(`/api/citations?client_id=${clientId}`);
  const [revision, setRevision] = useState(0);
  const reviews = useReviewPages(clientId, filter, revision);
  const [open, setOpen] = useState<Row | null>(null);
  const [citation, setCitation] = useState<Row | null>(null);
  const citationFields: Field[] = [
    { key: "listing_url", label: "Listing URL", type: "url" },
    { key: "listed_name", label: "Listed name" },
    { key: "listed_address", label: "Listed address" },
    { key: "listed_phone", label: "Listed phone" },
    { key: "listed_website", label: "Listed website", type: "url" },
    { key: "fix_notes", label: "Correction notes", type: "textarea" },
  ];

  if (brightLocal.loading || stats.loading || citations.loading || !reviews.loaded) return <PageSkeleton cards={4} />;
  if (stats.error) return <ErrorState message={stats.error} onRetry={stats.reload} />;
  if (citations.error) return <ErrorState message={citations.error} onRetry={citations.reload} />;

  const summary = stats.data?.summary;
  const total = summary?.total ?? 0;
  const links = brightLocal.data?.links ?? [];
  const citationRows = citations.data ?? [];
  const isAdmin = user?.role === "superadmin";
  const canSync = Boolean(brightLocal.data?.configured) && ["superadmin", "exec"].includes(user?.role || "");
  const linkTo = `/client-settings?client_id=${clientId}&section=brightlocal`;

  // Most clients have no BrightLocal link and nothing to show: one message and the one next step.
  if (!brightLocal.error && !total && !citationRows.length && !links.length) {
    return (
      <>
      <GoogleBusinessStatus clientId={clientId} onSynced={() => { stats.reload(); setRevision(n => n + 1); }} />
      <section className="panel">
        <EmptyState
          title="No reviews yet"
          description={`Connect Google Business Profile to import reviews and publish replies, or link BrightLocal to import reviews from other sites.${isAdmin ? "" : " Ask an admin to link the location."}`}
          action={isAdmin ? <Link className="button" to={linkTo}>Link BrightLocal</Link> : undefined}
        />
      </section>
      </>
    );
  }

  const byLocation = stats.data?.by_location ?? [];
  const imported = Object.fromEntries(byLocation.map((l) => [l.market_name, l.total]));
  const showLocation = byLocation.length > 1;
  const lowRated = summary?.low_rated ?? 0;
  const replyNeeded = summary?.reply_needed ?? 0;
  const openIssues = citationRows.reduce((sum, row) => sum + Number(row.open_issues || 0), 0);
  const listTotal = filter === "low" ? lowRated : filter === "reply" ? replyNeeded : total;
  const reports = links
    .map((l) => ({ id: l.bl_location_id, name: l.location_name, url: safeLink(l.summary?.citations?.url) }))
    .filter((r): r is { id: number; name: string; url: string } => Boolean(r.url));
  const showCitations = citationRows.length > 0 || Boolean(brightLocal.error) || !links.length;
  const refresh = () => {
    brightLocal.reload();
    stats.reload();
    citations.reload();
    setRevision((n) => n + 1);
  };
  const setFilter = (value: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("rating");
      next.delete("reply");
      if (value === "low") next.set("rating", "low");
      if (value === "reply") next.set("reply", "needed");
      return next;
    }, { replace: true });

  return (
    <>
      <GoogleBusinessStatus clientId={clientId} onSynced={refresh} />
      {brightLocal.error ? (
        <ErrorState title="BrightLocal couldn't load" message={brightLocal.error} onRetry={brightLocal.reload} />
      ) : links.length ? (
        <BrightLocalPanel clientId={clientId} data={brightLocal.data!} imported={imported} onSynced={refresh} />
      ) : (
        <p className="inline-notice reputation-unlinked">
          <span>
            BrightLocal isn't linked for this client. Link it to import reviews from additional sites.{" "}
            {isAdmin ? <Link to={linkTo}>Link BrightLocal</Link> : "Ask an admin to link it."}
          </span>
        </p>
      )}
      <div className="module-metrics reputation-metrics">
        <MetricCard label="Reviews" value={total.toLocaleString()} detail="Across connected review sources" />
        {/* Two decimals: 4.96 rounded to "5.0" read as perfect beside the reviews rated 3 or less. */}
        <MetricCard label="Average rating" value={summary?.avg_rating != null ? summary.avg_rating.toFixed(2) : "—"} detail="Across all reviews" />
        <MetricCard label="Last 30 days" value={(summary?.last_30_days ?? 0).toLocaleString()} detail="New reviews" />
        <MetricCard
          label="Rated 3 or less"
          value={lowRated.toLocaleString()}
          detail={replyNeeded ? `${replyNeeded.toLocaleString()} ${replyNeeded === 1 ? "needs" : "need"} a reply` : "None need a reply"}
          to={lowOnly || !lowRated ? undefined : `/reputation?client_id=${clientId}&rating=low`}
        />
        {citationRows.length > 0 && (
          <MetricCard label="Citations" value={citationRows.length.toLocaleString()} detail={openIssues ? `${openIssues.toLocaleString()} ${openIssues === 1 ? "needs" : "need"} correcting` : "None need correcting"} />
        )}
      </div>
      {/* With BrightLocal linked and no citation records of NOLA's own, BrightLocal's Citations card is the one
          place for citations (it links the Citation Tracker report), so no second card repeats it. */}
      <div className={`module-split reputation-split${showCitations ? "" : " is-single"}`}>
        <section className="panel reputation-reviews" aria-busy={reviews.loading}>
          <div className="panel-heading">
            <div><h2>Latest reviews</h2></div>
            <FilterPill
              label="Show"
              value={filter}
              onChange={setFilter}
              options={[
                { value: "", name: "All reviews", count: total },
                { value: "low", name: "3 stars or less", count: lowRated },
                { value: "reply", name: "Reply needed", count: replyNeeded },
              ]}
            />
          </div>
          {reviews.error && !reviews.rows.length ? (
            <ErrorState message={reviews.error} onRetry={reviews.retry} />
          ) : !reviews.rows.length ? (
            <p className="reputation-empty">
              {filter === "low" ? "No reviews rated 3 stars or less." : filter === "reply" ? "No review is waiting on a reply." : canSync ? "No reviews imported yet. Press Sync now to pull them in." : "No reviews imported yet."}
            </p>
          ) : (
            <ul className={`rv-list${reviews.loading ? " is-loading" : ""}`}>
              {reviews.rows.map((review) => {
                const rating = Number(review.rating) || 0;
                const text = String(review.review_text || "").trim();
                const platform = reviewPlatform(review.platform);
                const url = safeLink(review.review_url);
                const reply = String(review.response_status || "");
                const name = String(review.reviewer_name || "Anonymous reviewer");
                return (
                  <li key={String(review.id)} className="rv-row">
                    <div className="rv-head">
                      <button type="button" className="rv-open" onClick={() => setOpen(review)} aria-label={`Open ${name}'s review and reply`}>{name}</button>
                      {reply === "pending" && <StatusBadge tone="warning">Reply needed</StatusBadge>}
                      {(reply === "drafted" || reply === "approved") && <StatusBadge tone="info">Reply drafted</StatusBadge>}
                      {reply === "posted" && <StatusBadge tone="success">{review.response_source === "google" ? "Replied on Google" : "Recorded as replied"}</StatusBadge>}
                    </div>
                    <div className="rv-meta">
                      <span className="rv-stars" role="img" aria-label={starsText(rating)}>
                        {"★".repeat(rating)}<span className="rv-stars-off">{"★".repeat(Math.max(0, 5 - rating))}</span>
                      </span>
                      <span>{formatDate(String(review.review_date || ""))}</span>
                      <span>{platform}</span>
                      {showLocation && Boolean(review.market_name) && <span>{String(review.market_name)}</span>}
                      {url && <a href={url} target="_blank" rel="noopener noreferrer">{platform === "Other" ? "View review" : `View on ${platform}`}</a>}
                    </div>
                    {text ? <p className="rv-text">{text}</p> : <p className="rv-text muted">Rating only</p>}
                  </li>
                );
              })}
            </ul>
          )}
          {reviews.error && reviews.rows.length > 0 && <p className="inline-notice danger" role="alert">{reviews.error}</p>}
          {reviews.rows.length > 0 && (
            <div className="show-more">
              <span className="muted">Showing {reviews.rows.length.toLocaleString()} of {Math.max(listTotal, reviews.rows.length).toLocaleString()}</span>
              {reviews.more && reviews.rows.length < listTotal && (
                <button type="button" className="button secondary" disabled={reviews.loadingMore || reviews.loading} onClick={() => void reviews.loadMore()}>
                  {reviews.loadingMore ? "Loading…" : "Show more"}
                </button>
              )}
            </div>
          )}
        </section>
        {showCitations && <section className="panel reputation-citations">
          <div className="panel-heading">
            <div><h2>Citation inventory</h2></div>
          </div>
          {citationRows.length ? (
            <ul className="module-list">
              {citationRows.map((row, index) => (
                <li key={String(row.id || index)}>
                  <button className="record-open" onClick={() => setCitation(row)}>
                    {String(row.directory_name || "Directory")}
                  </button>
                  <span>{sentence(String(row.listing_status || "not_checked"))}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="reputation-citations-empty">
              <p>{reports.length ? "Citations are tracked in BrightLocal." : "No citation records yet."}</p>
              {reports.map((report) => (
                <a key={report.id} href={report.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink aria-hidden="true" />
                  Open Citation Tracker report{reports.length > 1 ? ` (${report.name})` : ""}
                </a>
              ))}
            </div>
          )}
        </section>}
      </div>
      {open && (
        <ReviewDialog
          review={open}
          onClose={() => setOpen(null)}
          onSaved={(patch) => {
            reviews.setRows((rows) => rows.map((row) => (row.id === open.id ? { ...row, ...patch } : row)));
            stats.reload();
          }}
        />
      )}
      {citation && (
        <RecordForm
          title="Citation details"
          endpoint={`/api/citations/${citation.id}`}
          method="PUT"
          fields={citationFields}
          initial={citation}
          onClose={() => setCitation(null)}
          onSaved={() => citations.reload()}
        />
      )}
    </>
  );
}

export function ReputationPage() {
  const { clients, clientId, client } = useClientSelection();
  return (
    <div className="reputation-page">
      <PageHeader
        eyebrow="Reputation"
        title="Reviews & citations"
        description={client ? `Google reviews and directory listings for ${client.name}.` : "Google reviews and directory listings."}
      />
      {clients.loading ? (
        <PageSkeleton cards={4} />
      ) : clients.error ? (
        <ErrorState message={clients.error} onRetry={clients.reload} />
      ) : clientId === null ? (
        <section className="panel">
          <EmptyState
            title="No clients available"
            description="Client records will appear here once you have access to a client."
          />
        </section>
      ) : (
        <ReputationData key={clientId} clientId={clientId} />
      )}
    </div>
  );
}
